import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as ordiniLavoroApi from "../../../../../../api/pianificazione/ordiniLavoroApi";
import { toast } from "react-toastify";
import {DateTime} from "luxon";
import {OrdineLavoro} from "../../../../produzione/ordiniLavoro/ordiniLavoroTypes";

// ============================================
// INTERFACES
// ============================================
//
// export interface OrdineLavoro {
//     id?: number;
//     previsione?: number;  // Solo ID
//     stato?: string;
//     qta_prevista?: number | string;
//     data_arrivo_prevista ?: string | Date | null;
//     data_consegna_prevista?: string | Date | null;
//     qta_arrivata?: number | string;
//     data_ricevimento?: string | Date | null;
//     note?: string;
//     // Campi calcolati dal backend
//     created_at?: string;
//     updated_at?: string;
//     errorsStack?: ErrorsStack;
// }

export interface OrdiniLavoroList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: OrdineLavoro[];
    errorsStack: ErrorsStack;
}

// ============================================
// STATO SLICE
// ============================================

export interface OrdiniLavoroState {
    // Organizziamo gli ordiniLavoro per previsione_id
    ordiniLavoroByPrevisione: {
        [previsioneId: number]: OrdiniLavoroList;
    };
    ordineLavoroCorrenteId?: number;
    previsioneCorrenteId?: number;  // Per sapere su quale previsione stiamo lavorando
}

const initialState: OrdiniLavoroState = {
    ordiniLavoroByPrevisione: {},
    ordineLavoroCorrenteId: undefined,
    previsioneCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchOrdiniLavoroByPrevisione = createAsyncThunk(
    "ordiniLavoroByPrevisione/fetchOrdiniLavoroByPrevisione",
    async (parametri: { previsioneId: number }) => {
        const response = await ordiniLavoroApi.fetchOrdiniLavoroByPrevisione(parametri.previsioneId);
        return { previsioneId: parametri.previsioneId, data: response };
    }
);

export const getOrdiniLavoro = createAsyncThunk(
    "ordiniLavoroByPrevisione/getOrdiniLavoro",
    async (parametri: { previsioneId: number; ordineLavoroId: number }) => {
        const response = await ordiniLavoroApi.getOrdiniLavoro(
            parametri.previsioneId,
            parametri.ordineLavoroId
        );
        return { previsioneId: parametri.previsioneId, data: response };
    }
);

export const saveOrdiniLavoro = createAsyncThunk(
    "ordiniLavoroByPrevisione/saveOrdiniLavoro",
    async (parametri: {
        previsioneId: number;
        ordineLavoroToSave: OrdineLavoro;
        azione?: string;
    }) => {
        const response = await ordiniLavoroApi.saveOrdiniLavoro(
            parametri.previsioneId,
            parametri.ordineLavoroToSave,
            parametri.azione
        );
        return { previsioneId: parametri.previsioneId, data: response };
    }
);

export const deleteOrdiniLavoro = createAsyncThunk(
    "ordiniLavoroByPrevisione/deleteOrdiniLavoro",
    async (parametri: { previsioneId: number; ordineLavoroToDelete: OrdineLavoro }) => {
        await ordiniLavoroApi.deleteOrdiniLavoro(
            parametri.previsioneId,
            parametri.ordineLavoroToDelete
        );
        return {
            previsioneId: parametri.previsioneId,
            ordineLavoroId: parametri.ordineLavoroToDelete.id
        };
    }
);

// Azioni speciali
export const confermaArrivoOrdiniLavoro = createAsyncThunk(
    "ordiniLavoroByPrevisione/confermaArrivoOrdiniLavoro",
    async (parametri: {
        previsioneId: number;
        ordineLavoro: OrdineLavoro;
        qta_arrivata?: number;
    }) => {
        const ordineLavoroToSave = {
            ...parametri.ordineLavoro,
            qta_arrivata: parametri.qta_arrivata || parametri.ordineLavoro.qta_prevista
        };
        const response = await ordiniLavoroApi.saveOrdiniLavoro(
            parametri.previsioneId,
            ordineLavoroToSave,
            'conferma_arrivo'
        );
        return { previsioneId: parametri.previsioneId, data: response };
    }
);

export const completaOrdiniLavoro = createAsyncThunk(
    "ordiniLavoroByPrevisione/completaOrdiniLavoro",
    async (parametri: { previsioneId: number; ordineLavoro: OrdineLavoro }) => {
        const response = await ordiniLavoroApi.saveOrdiniLavoro(
            parametri.previsioneId,
            parametri.ordineLavoro,
            'completa'
        );
        return { previsioneId: parametri.previsioneId, data: response };
    }
);

// ============================================
// SLICE
// ============================================

export const ordiniLavoroByPrevisioneSlice = createSlice({
    name: "ordiniLavoro",
    initialState,
    reducers: {
        setOrdiniLavoroCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.ordineLavoroCorrenteId = action.payload;
        },
        setPrevisioneCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.previsioneCorrenteId = action.payload;
        },
        resetOrdiniLavoroByPrevisione: (state, action: PayloadAction<number>) => {
            // Reset ordiniLavoro per una specifica previsione
            delete state.ordiniLavoroByPrevisione[action.payload];
        },
        resetOrdiniLavoro: (state) => {
            state.ordiniLavoroByPrevisione = {};
            state.ordineLavoroCorrenteId = undefined;
            state.previsioneCorrenteId = undefined;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH IMPEGNI BY PREVISIONE
        // ============================================
        builder.addCase(fetchOrdiniLavoroByPrevisione.pending, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (!state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId] = {
                    count: 0,
                    page: 0,
                    num_pages: 0,
                    results: [],
                    errorsStack: { status: ErrorStatusTypes.PENDING }
                };
            } else {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.PENDING
                };
            }
        });
        builder.addCase(fetchOrdiniLavoroByPrevisione.fulfilled, (state, action) => {
            const { previsioneId, data } = action.payload;
            state.ordiniLavoroByPrevisione[previsioneId] = {
                ...data,
                errorsStack: { status: ErrorStatusTypes.OK }
            };
        });
        builder.addCase(fetchOrdiniLavoroByPrevisione.rejected, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = parseErrorMessage(action.error);
            }
        });

        // ============================================
        // GET IMPEGNO
        // ============================================
        builder.addCase(getOrdiniLavoro.pending, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.PENDING
                };
            }
        });
        builder.addCase(getOrdiniLavoro.fulfilled, (state, action) => {
            const { previsioneId, data } = action.payload;
            if (!state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId] = {
                    count: 1,
                    page: 1,
                    num_pages: 1,
                    results: [data],
                    errorsStack: { status: ErrorStatusTypes.OK }
                };
            } else {
                // Rimuove versione esistente
                state.ordiniLavoroByPrevisione[previsioneId].results =
                    state.ordiniLavoroByPrevisione[previsioneId].results.filter(
                        (item) => item.id !== data.id
                    );
                // Aggiunge versione aggiornata
                state.ordiniLavoroByPrevisione[previsioneId].results.push(data);
                // Ordina per data_consegna_prevista poi per id
                state.ordiniLavoroByPrevisione[previsioneId].results.sort((a, b) => {
                    if (a.data_consegna_prevista && b.data_consegna_prevista) {
                        const dateA = new Date(a.data_consegna_prevista).getTime();
                        const dateB = new Date(b.data_consegna_prevista).getTime();
                        if (dateA !== dateB) return dateA - dateB;
                    }
                    return (a.id || 0) - (b.id || 0);
                });
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.OK
                };
            }
            state.ordineLavoroCorrenteId = data.id;
        });
        builder.addCase(getOrdiniLavoro.rejected, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = parseErrorMessage(action.error);
            }
        });

        // ============================================
        // SAVE IMPEGNO - NO TOAST (usa AutoSaveIndicator)
        // ============================================
        builder.addCase(saveOrdiniLavoro.pending, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.PENDING
                };
            }
        });
        builder.addCase(saveOrdiniLavoro.fulfilled, (state, action) => {
            const { previsioneId, data } = action.payload;
            if (!state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId] = {
                    count: 1,
                    page: 1,
                    num_pages: 1,
                    results: [data],
                    errorsStack: { status: ErrorStatusTypes.SUCCESS }
                };
            } else {
                const index = state.ordiniLavoroByPrevisione[previsioneId].results.findIndex(
                    (item) => item.id === data.id
                );

                if (index !== -1) {
                    // Update esistente
                    state.ordiniLavoroByPrevisione[previsioneId].results[index] = {
                        ...data,
                        errorsStack: { status: ErrorStatusTypes.SUCCESS },
                    };
                } else {
                    // Nuovo elemento
                    state.ordiniLavoroByPrevisione[previsioneId].results.push({
                        ...data,
                        errorsStack: { status: ErrorStatusTypes.SUCCESS },
                    });
                    state.ordiniLavoroByPrevisione[previsioneId].count++;
                }

                // Ordina per data_consegna_prevista poi per id
                state.ordiniLavoroByPrevisione[previsioneId].results.sort((a, b) => {
                    if (a.data_consegna_prevista && b.data_consegna_prevista) {
                        const dateA = new Date(a.data_consegna_prevista).getTime();
                        const dateB = new Date(b.data_consegna_prevista).getTime();
                        if (dateA !== dateB) return dateA - dateB;
                    }
                    return (a.id || 0) - (b.id || 0);
                });

                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.SUCCESS
                };
            }
            state.ordineLavoroCorrenteId = data.id;
        });
        builder.addCase(saveOrdiniLavoro.rejected, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            const ordineLavoroId = action.meta.arg.ordineLavoroToSave.id;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                // Aggiorna errore sull'elemento specifico se esiste
                if (ordineLavoroId) {
                    state.ordiniLavoroByPrevisione[previsioneId].results =
                        state.ordiniLavoroByPrevisione[previsioneId].results.map((item) => {
                            if (item.id === ordineLavoroId) {
                                return {
                                    ...item,
                                    errorsStack: parseErrorMessage(action.error),
                                };
                            }
                            return item;
                        });
                }
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = parseErrorMessage(action.error);
            }
        });

        // ============================================
        // DELETE IMPEGNO - OK Toast qui
        // ============================================
        builder.addCase(deleteOrdiniLavoro.pending, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.PENDING
                };
            }
        });
        builder.addCase(deleteOrdiniLavoro.fulfilled, (state, action) => {
            const { previsioneId, ordineLavoroId } = action.payload;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].results =
                    state.ordiniLavoroByPrevisione[previsioneId].results.filter(
                        (item) => item.id !== ordineLavoroId
                    );
                state.ordiniLavoroByPrevisione[previsioneId].count--;
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = {
                    status: ErrorStatusTypes.OK
                };
            }
            toast.success("OrdineLavoro eliminato con successo");
        });
        builder.addCase(deleteOrdiniLavoro.rejected, (state, action) => {
            const previsioneId = action.meta.arg.previsioneId;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                state.ordiniLavoroByPrevisione[previsioneId].errorsStack = parseErrorMessage(action.error);
            }
            toast.error("Errore nell'eliminazione dell'ordineLavoro");
        });

        // ============================================
        // AZIONI SPECIALI (conferma arrivo, completa)
        // ============================================
        // Conferma arrivo
        builder.addCase(confermaArrivoOrdiniLavoro.fulfilled, (state, action) => {
            const { previsioneId, data } = action.payload;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                const index = state.ordiniLavoroByPrevisione[previsioneId].results.findIndex(
                    (item) => item.id === data.id
                );
                if (index !== -1) {
                    state.ordiniLavoroByPrevisione[previsioneId].results[index] = data;
                }
            }
            toast.success("Arrivo confermato");
        });

        // Completa ordineLavoro
        builder.addCase(completaOrdiniLavoro.fulfilled, (state, action) => {
            const { previsioneId, data } = action.payload;
            if (state.ordiniLavoroByPrevisione[previsioneId]) {
                const index = state.ordiniLavoroByPrevisione[previsioneId].results.findIndex(
                    (item) => item.id === data.id
                );
                if (index !== -1) {
                    state.ordiniLavoroByPrevisione[previsioneId].results[index] = data;
                }
            }
            toast.success("OrdineLavoro completato");
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setOrdiniLavoroCorrenteId,
    setPrevisioneCorrenteId,
    resetOrdiniLavoroByPrevisione,
    resetOrdiniLavoro
} = ordiniLavoroByPrevisioneSlice.actions;

export const ordiniLavoroByPrevisioneSliceReducer = ordiniLavoroByPrevisioneSlice.reducer;